import { Flex, FlexProps, useResizeObserver, Box } from '@cad-arc';
import { debounce } from 'lodash';
import { useRef, useState, useEffect, useMemo } from 'react';
import { Tooltip } from './tooltip';
import * as textMetrics from 'text-metrics';

type Item = {
    id: number;
    label: string;
};

type ItemWidths = { [id: number]: number };

type MultiRowTruncateProps = FlexProps & {
    maxNumRows: number;
    items: Item[];
    expectedCharacterWidth: number;
};

const TRUNCATED_TOOLTIP_ONE_DIGIT_ID = -1;
const TRUNCATED_TOOLTIP_TWO_DIGITS_ID = -2;
const TRUNCATED_TOOLTIP_THREE_DIGIT_ID = -3;

const ITEM_PADDING_PX = 4;

const getItemWidths = (htmlElement: HTMLDivElement, items: Item[]) => {
    const containerMetrics = textMetrics.init(htmlElement);
    const itemWidths: ItemWidths = {};

    items.forEach(({ id, label }, index) => {
        const itemText = index === items.length - 1 ? label : `${label}, `;
        itemWidths[id] = containerMetrics.width(itemText) + ITEM_PADDING_PX;
    });

    itemWidths[TRUNCATED_TOOLTIP_ONE_DIGIT_ID] = containerMetrics.width('+9');
    itemWidths[TRUNCATED_TOOLTIP_TWO_DIGITS_ID] = containerMetrics.width('+99');
    itemWidths[TRUNCATED_TOOLTIP_THREE_DIGIT_ID] = containerMetrics.width('+999');

    return itemWidths;
};

const getTruncatedTooltipWidth = (itemsRemaining: number, itemWidths: ItemWidths) => {
    if (itemsRemaining < 10) {
        return itemWidths[TRUNCATED_TOOLTIP_ONE_DIGIT_ID];
    } else if (itemsRemaining < 100) {
        return itemWidths[TRUNCATED_TOOLTIP_TWO_DIGITS_ID];
    } else {
        return itemWidths[TRUNCATED_TOOLTIP_THREE_DIGIT_ID];
    }
};

const getRowTruncateIndex = (
    containerWidth: number,
    itemWidths: ItemWidths,
    items: Item[],
    rowItemOffset: number,
    isLastRow: boolean
) => {
    let usedWidth = 0;
    let rowTruncateIndex = 0;

    for (let itemIndex = rowItemOffset; itemIndex < items.length; itemIndex++) {
        const item = items[itemIndex];
        usedWidth += itemWidths[item.id];

        if (isLastRow) {
            const itemsRemaining = items.length - itemIndex;
            if (usedWidth > containerWidth - getTruncatedTooltipWidth(itemsRemaining, itemWidths)) {
                break;
            }
        } else {
            if (usedWidth > containerWidth) {
                break;
            }
        }

        rowTruncateIndex++;
    }

    return rowTruncateIndex;
};

const getTruncateIndex = (
    containerWidth: number,
    maxNumRows: number,
    itemWidths: ItemWidths,
    items: Item[]
) => {
    let truncateIndex = 0;
    let rowItemOffset = 0;

    for (let rowIndex = 0; rowIndex < maxNumRows; rowIndex++) {
        const isLastRow = rowIndex === maxNumRows - 1;

        const rowTruncateIndex = getRowTruncateIndex(
            containerWidth,
            itemWidths,
            items,
            rowItemOffset,
            isLastRow
        );

        rowItemOffset = rowTruncateIndex;
        truncateIndex += rowTruncateIndex;
    }

    return truncateIndex;
};

export function MultiRowTruncate(props: MultiRowTruncateProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { items, expectedCharacterWidth, maxNumRows, ...rest } = props;
    const [truncateIndex, setTruncateIndex] = useState(0);
    const [itemWidths, setItemWidths] = useState<{ [id: number]: number }>();
    const [padding, setPadding] = useState<number>();
    const itemsToShow = items.slice(0, truncateIndex);
    const itemsToTruncate = items.slice(truncateIndex);

    useEffect(() => {
        if (containerRef.current !== null && containerRef.current?.clientWidth !== undefined) {
            const { paddingLeft, paddingRight } = window.getComputedStyle(
                containerRef.current,
                null
            );
            const padding =
                parseInt(paddingLeft.replace('px', '')) + parseInt(paddingRight.replace('px', ''));
            setPadding(padding);
        }
    }, [containerRef?.current?.clientWidth, items, maxNumRows]);

    useEffect(() => {
        if (
            containerRef.current !== null &&
            containerRef.current?.clientWidth !== undefined &&
            padding !== undefined &&
            itemWidths === undefined
        ) {
            const itemWidths = getItemWidths(containerRef.current, items);
            setItemWidths(itemWidths);
            const containerWidth = containerRef.current.clientWidth - padding;
            const truncateIndex = getTruncateIndex(containerWidth, maxNumRows, itemWidths, items);
            setTruncateIndex(truncateIndex);
        }
    }, [containerRef.current?.clientWidth, itemWidths, items, maxNumRows, padding]);

    const debouncedOnResize = useMemo(
        () =>
            debounce((size) => {
                if (
                    size.width &&
                    containerRef.current !== null &&
                    itemWidths !== undefined &&
                    padding !== undefined
                ) {
                    const containerWidth = containerRef.current.clientWidth - padding;
                    const truncateIndex = getTruncateIndex(
                        containerWidth,
                        maxNumRows,
                        itemWidths,
                        items
                    );
                    setTruncateIndex(truncateIndex);
                }
            }, 50),
        [itemWidths, items, maxNumRows, padding]
    );

    useResizeObserver({
        ref: containerRef,
        onResize: debouncedOnResize,
    });

    useEffect(() => {
        return () => {
            debouncedOnResize.cancel();
        };
    }, [debouncedOnResize]);

    return (
        <Flex {...rest} ref={containerRef}>
            {itemsToShow.map((item, index) => {
                return (
                    <Box key={item.id} pr="var(--arc-space-1)">
                        {`${item.label}${index < itemsToShow.length - 1 ? ', ' : ''}`}
                    </Box>
                );
            })}

            {itemsToTruncate.length > 0 && (
                <Tooltip
                    overlay={itemsToTruncate.map((item, index) => {
                        return (
                            <Box key={item.id} pr="var(--arc-space-1)">
                                {`${item.label}${index < itemsToTruncate.length - 1 ? ', ' : ''}`}
                            </Box>
                        );
                    })}
                >
                    <Box cursor="default">+{itemsToTruncate.length}</Box>
                </Tooltip>
            )}
        </Flex>
    );
}
